import React, { useMemo } from 'react';
import { Card, Flex, Typography, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import type { AssociationConfig, AssociationItem } from '../types';
import './AssociationLessonTeacher.css';

const { Title, Text } = Typography;

// Helper function to get text by current language
const getTextByLanguage = (item: AssociationItem, currentLang?: string): string => {
  const lang = currentLang || (typeof window !== 'undefined' && localStorage.getItem('i18nextLng')) || 'en';
  const langKey = lang.startsWith('fr') ? 'fr' : lang.startsWith('ar') ? 'ar' : 'en';
  return item[`text_${langKey}` as keyof AssociationItem] as string || '';
};

// Helper function to get title by current language
const getTitleByLanguage = (config: AssociationConfig, side: 'left' | 'right', currentLang?: string): string => {
  const lang = currentLang || (typeof window !== 'undefined' && localStorage.getItem('i18nextLng')) || 'en';
  const langKey = lang.startsWith('fr') ? 'fr' : lang.startsWith('ar') ? 'ar' : 'en';
  const titleKey = `${side}Title_${langKey}` as keyof AssociationConfig;
  return (config[titleKey] as string) || '';
};

type Props = {
  config: AssociationConfig;
  statement?: string;
};

const AssociationLessonTeacher: React.FC<Props> = ({ config, statement }) => {
  const { t, i18n } = useTranslation();
  const safeConfig = useMemo(
    () => ({ ...config, left: config.left || [], right: config.right || [] }),
    [config],
  );

  return (
    <div className="association-teacher">
      <Card>
        <Flex vertical gap={20}>
          {(statement || safeConfig.statement) && (
            <div className="association-statement">
              {statement || safeConfig.statement}
            </div>
          )}
          <Flex gap={16} align="stretch" className="association-columns">
            <div className="association-column">
              <Title level={5}>{getTitleByLanguage(safeConfig, 'left', i18n.language) || t('associationLesson.teacher.leftTitle') || 'Colonne A'}</Title>
              <Flex vertical gap={16}>
                {safeConfig.left.map((item) => (
                  <Card key={item.id} className="association-card">
                    <Flex vertical gap={8} align="center">
                      {item.image && (
                        <div className="association-image-container">
                          <img src={item.image} alt={getTextByLanguage(item, i18n.language) || item.id} className="association-image" />
                        </div>
                      )}
                      {getTextByLanguage(item, i18n.language) && <Text strong>{getTextByLanguage(item, i18n.language)}</Text>}
                      <Tag color="blue">{item.matchKey}</Tag>
                    </Flex>
                  </Card>
                ))}
              </Flex>
            </div>
            <div className="association-column">
              <Title level={5}>{getTitleByLanguage(safeConfig, 'right', i18n.language) || t('associationLesson.teacher.rightTitle') || 'Colonne B'}</Title>
              <Flex vertical gap={16}>
                {safeConfig.right.map((item) => (
                  <Card key={item.id} className="association-card">
                    <Flex vertical gap={8} align="center">
                      {item.image && (
                        <div className="association-image-container">
                          <img src={item.image} alt={getTextByLanguage(item, i18n.language) || item.id} className="association-image" />
                        </div>
                      )}
                      {getTextByLanguage(item, i18n.language) && <Text>{getTextByLanguage(item, i18n.language)}</Text>}
                      <Tag color="green">{item.matchKey}</Tag>
                    </Flex>
                  </Card>
                ))}
              </Flex>
            </div>
          </Flex>
        </Flex>
      </Card>
    </div>
  );
};

export default AssociationLessonTeacher;
